/**
 * Minimap (D130): the whole map as an isometric diamond, like the strategy
 * games it follows — terrain drawn once, then buildings, units, the infected
 * and the camera frame redrawn a few times a second. Click / drag to jump.
 * DOM canvas over the game, so it costs the Phaser renderer nothing.
 */
export interface MinimapSource {
  readonly width: number;
  readonly height: number;
  terrainCss(x: number, y: number): string;
  buildings(): readonly { x: number; y: number; w: number; h: number; css: string }[];
  units(): readonly { x: number; y: number; css: string }[];
  /** Camera view as the four screen corners in grid coordinates. */
  view(): readonly { x: number; y: number }[];
  jump(x: number, y: number): void;
}

export class Minimap {
  private readonly canvas: HTMLCanvasElement;
  private readonly terrain: HTMLCanvasElement;
  private readonly W = 260;
  private readonly H = 130;
  private readonly n: number;
  private timer = 0;
  private dragging = false;
  /** Frames drawn (tests). */
  frames = 0;

  constructor(
    host: HTMLElement,
    private readonly src: MinimapSource,
  ) {
    this.n = Math.max(src.width, src.height);
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.W * dpr;
    this.canvas.height = this.H * dpr;
    this.canvas.className = 'minimap-canvas';
    this.canvas.setAttribute('aria-label', 'Minimap');
    this.terrain = document.createElement('canvas');
    this.terrain.width = this.canvas.width;
    this.terrain.height = this.canvas.height;
    host.appendChild(this.canvas);
    this.drawTerrain(dpr);
    this.canvas.addEventListener('pointerdown', this.onDown);
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    this.tick();
  }

  destroy(): void {
    window.clearTimeout(this.timer);
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    this.canvas.remove();
  }

  /** Grid → minimap pixels (CSS px). */
  private toMap(x: number, y: number): [number, number] {
    const k = this.W / (2 * this.n);
    return [(x - y) * k + this.W / 2, (x + y) * (k / 2) + (this.H - (this.src.width + this.src.height) * (k / 2)) / 2];
  }

  private toGrid(px: number, py: number): [number, number] {
    const k = this.W / (2 * this.n);
    const off = (this.H - (this.src.width + this.src.height) * (k / 2)) / 2;
    const a = (px - this.W / 2) / k;
    const b = (py - off) / (k / 2);
    return [(a + b) / 2, (b - a) / 2];
  }

  private drawTerrain(dpr: number): void {
    const g = this.terrain.getContext('2d');
    if (!g) return;
    g.scale(dpr, dpr);
    for (let y = 0; y < this.src.height; y++) {
      for (let x = 0; x < this.src.width; x++) {
        g.fillStyle = this.src.terrainCss(x, y);
        const [a, b] = this.toMap(x, y);
        const [c, d] = this.toMap(x + 1, y);
        const [e, f] = this.toMap(x + 1, y + 1);
        const [h, i] = this.toMap(x, y + 1);
        g.beginPath();
        g.moveTo(a, b);
        g.lineTo(c, d);
        g.lineTo(e, f);
        g.lineTo(h, i);
        g.closePath();
        g.fill();
      }
    }
  }

  private tick = (): void => {
    this.draw();
    this.timer = window.setTimeout(this.tick, 350);
  };

  draw(): void {
    const g = this.canvas.getContext('2d');
    if (!g) return;
    const dpr = this.canvas.width / this.W;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, this.canvas.width, this.canvas.height);
    g.drawImage(this.terrain, 0, 0);
    g.scale(dpr, dpr);
    for (const b of this.src.buildings()) {
      const [x, y] = this.toMap(b.x + b.w / 2, b.y + b.h / 2);
      const s = Math.max(3, (b.w * this.W) / (2 * this.n));
      g.fillStyle = '#0a0a0a';
      g.fillRect(x - s / 2 - 1, y - s / 4 - 1, s + 2, s / 2 + 2);
      g.fillStyle = b.css;
      g.fillRect(x - s / 2, y - s / 4, s, s / 2);
    }
    for (const u of this.src.units()) {
      const [x, y] = this.toMap(u.x, u.y);
      g.fillStyle = u.css;
      g.fillRect(x - 1, y - 1, 2.2, 2.2);
    }
    const v = this.src.view();
    if (v.length === 4) {
      g.strokeStyle = '#f6efd6';
      g.lineWidth = 1.2;
      g.beginPath();
      v.forEach((p, i) => {
        const [x, y] = this.toMap(p.x, p.y);
        if (i) g.lineTo(x, y);
        else g.moveTo(x, y);
      });
      g.closePath();
      g.stroke();
    }
    this.frames++;
  }

  private jumpTo(e: PointerEvent): void {
    const r = this.canvas.getBoundingClientRect();
    const [x, y] = this.toGrid(((e.clientX - r.left) / r.width) * this.W, ((e.clientY - r.top) / r.height) * this.H);
    this.src.jump(Math.max(0, Math.min(this.src.width - 1, x)), Math.max(0, Math.min(this.src.height - 1, y)));
    this.draw();
  }

  private readonly onDown = (e: PointerEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    this.dragging = true;
    this.jumpTo(e);
  };

  private readonly onMove = (e: PointerEvent): void => {
    if (this.dragging) this.jumpTo(e);
  };

  private readonly onUp = (): void => {
    this.dragging = false;
  };
}
